import { useState } from 'react';
import type { TagDef } from '../utils/questionTag';

/** 知识点标签列：两级结构——一级分类（点击展开）+ 二级标签，点击标注/取消（readOnly 仅展示已标注） */
export default function QuestionTagColumn({ tags, marks, contentKey, onToggle, readOnly, manageUrl }: {
  tags: TagDef[];
  marks: Record<string, string[]>;
  contentKey: string;
  onToggle?: (contentKey: string, tagId: string) => void;
  readOnly?: boolean;
  manageUrl?: string;
}) {
  const selected = marks[contentKey] || [];
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  if (readOnly) {
    // 只读：仅展示已标注标签
    const selectedTags = tags.filter(t => selected.includes(t.id));
    if (selectedTags.length === 0) return null;
    return (
      <div className="flex lg:flex-col flex-wrap gap-1.5 lg:w-28 shrink-0">
        {selectedTags.map(t => (
          <span key={t.id} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white whitespace-nowrap"
            style={{ backgroundColor: t.color || '#3B82F6' }}>{t.name}</span>
        ))}
      </div>
    );
  }
  if (tags.length === 0) {
    return (
      <div className="hidden lg:flex lg:flex-col lg:w-28 shrink-0 text-xs text-gray-400 leading-5">
        暂无标签
        {manageUrl && <a href={manageUrl} className="text-blue-500 hover:underline mt-1">去维护标签库</a>}
      </div>
    );
  }
  // 按 category 分组（保持传入顺序），无 category 的归入"未分类"
  const categoryOrder: string[] = [];
  const groupMap: Record<string, TagDef[]> = {};
  tags.forEach(t => {
    const cat = t.category || '未分类';
    if (!groupMap[cat]) { groupMap[cat] = []; categoryOrder.push(cat); }
    groupMap[cat].push(t);
  });
  return (
    <div className="flex flex-row-reverse items-start gap-2 p-3 rounded-xl bg-white/95 backdrop-blur shadow-lg border border-gray-100">
      {/* 一级分类列（贴右缘） */}
      <div className="flex flex-col gap-2">
      {categoryOrder.map(cat => {
        const groupTags = groupMap[cat];
        const groupColor = groupTags[0]?.color || '#3B82F6';
        const selectedInGroup = groupTags.filter(t => selected.includes(t.id));
        const expanded = expandedCategory === cat;
        return (
          <div key={cat} className="flex flex-col">
            <button
              onClick={() => setExpandedCategory(expanded ? null : cat)}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-base font-semibold border transition-colors ${
                selectedInGroup.length > 0
                  ? 'text-white border-transparent shadow-sm'
                  : 'text-gray-700 hover:shadow-sm'
              }`}
              style={selectedInGroup.length > 0
                ? { backgroundColor: groupColor, borderLeft: `5px solid rgba(255,255,255,.55)` }
                : { backgroundColor: `${groupColor}14`, borderColor: `${groupColor}33`, borderLeft: `5px solid ${groupColor}`, color: groupColor }}
            >
              <span className={`w-2.5 h-2.5 rounded-sm shrink-0 ${selectedInGroup.length > 0 ? 'bg-white/90' : ''}`}
                style={selectedInGroup.length > 0 ? undefined : { backgroundColor: groupColor }} />
              <span className="whitespace-nowrap">{cat}</span>
              <span className="flex items-center gap-1 ml-auto">
                {selectedInGroup.length > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full text-sm leading-none font-bold ${
                    expanded ? 'text-white bg-white/30' : 'bg-white text-current'
                  }`}
                    style={expanded ? undefined : { color: groupColor }}>{selectedInGroup.length}</span>
                )}
                <span className={`transition-transform text-xs font-bold ${expanded ? 'rotate-180' : ''}`}
                  style={selectedInGroup.length > 0 ? { color: 'rgba(255,255,255,.9)' } : { color: groupColor }}>◀</span>
              </span>
            </button>
          </div>
        );
      })}
      </div>
      {/* 二级标签列：展开后显示在一级分类左侧 */}
      {expandedCategory && groupMap[expandedCategory] && (
        <div className="flex flex-col gap-1.5 p-2 rounded-lg border border-gray-100 bg-gray-50/80 max-h-[70vh] overflow-y-auto">
          {groupMap[expandedCategory].map(t => {
            const isSelected = selected.includes(t.id);
            return (
              <button
                key={t.id}
                onClick={() => onToggle?.(contentKey, t.id)}
                title={isSelected ? '点击取消标注' : '点击标注'}
                className={`px-3 py-1 rounded-full text-base font-medium whitespace-nowrap border transition-colors ${
                  isSelected
                    ? 'text-white border-transparent shadow-sm'
                    : 'bg-white text-gray-700 hover:shadow-sm'
                }`}
                style={isSelected
                  ? { backgroundColor: t.color || '#3B82F6' }
                  : { borderColor: `${t.color || '#3B82F6'}55`, borderLeft: `4px solid ${t.color || '#3B82F6'}` }}
              >
                {t.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
